import { FunctionComponent, MouseEvent, useState, PropsWithChildren } from 'react';
import { useExtendedIntl } from 'hooks/useExtendedIntl';
import Stars from '@mui/icons-material/Stars';
import MoreVert from '@mui/icons-material/MoreVert';
import { useIsMobileResolution } from 'hooks/useIsMobileResolution';
import { PermissionsViewDecider } from 'shared/PermissionsViewDecider';
import { APIPermission } from 'typeDeclarations/enums';
import { TeamUser } from 'shared/TeamForm/types';

import {
  Menu,
  Chip,
  Avatar,
  ListItem,
  MenuItem,
  IconButton,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
} from '@mui/material';

interface TeamUserListItemProps {
  canEdit?: boolean;
  isAdmin?: boolean;
  teamUser: TeamUser;
  handleMakeAdmin: (owner: TeamUser) => void;
  handleDeleteTeamUser: (owner: TeamUser) => void;
}

export const TeamUserListItem: FunctionComponent<PropsWithChildren<TeamUserListItemProps>> = ({
  teamUser,
  canEdit = false,
  isAdmin = false,
  handleMakeAdmin,
  handleDeleteTeamUser,
}) => {
  const { formatMessage } = useExtendedIntl();
  const isMobile = useIsMobileResolution();

  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(userMenuAnchor);

  const handleCloseUserMenu = () => setUserMenuAnchor(null);

  const handleOpenUserMenu = (e: MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(e.currentTarget);
  };

  return (
    <ListItem disableGutters>
      <ListItemAvatar sx={{ display: 'flex', justifyContent: 'center' }}>
        <Avatar src={teamUser.picture?.url} imgProps={{ loading: 'lazy' }}>
          {teamUser.picture ? teamUser.name[0]?.toUpperCase() || teamUser.email[0] || 'U' : null}
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={teamUser.name}
        secondary={teamUser.email}
        primaryTypographyProps={{ noWrap: true }}
        secondaryTypographyProps={{ noWrap: true }}
      />
      <PermissionsViewDecider requiredPermissions={[APIPermission.PerformChanges]}>
        {isAdmin ? (
          <Chip
            icon={<Stars />}
            size={isMobile ? 'small' : 'medium'}
            label={formatMessage({ id: 'shared.admin' })}
            sx={(theme) => ({
              backgroundColor: 'rgb(61, 58, 189)',
              marginRight: '1rem',
              '.MuiChip-label': {
                color: theme.palette.common.white,
              },
              '.MuiChip-icon': {
                color: theme.palette.common.white,
              },
            })}
          />
        ) : (
          canEdit && (
            <ListItemSecondaryAction sx={{ right: '1rem' }}>
              <IconButton onClick={handleOpenUserMenu} size="large">
                <MoreVert />
              </IconButton>
              <Menu
                id="long-menu"
                open={menuOpen}
                anchorEl={userMenuAnchor}
                onClose={handleCloseUserMenu}
                TransitionProps={{ mountOnEnter: true }}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
              >
                <MenuItem onClick={() => handleMakeAdmin(teamUser)}>
                  {formatMessage({ id: 'team-form.make-admin' })}
                </MenuItem>
                <MenuItem onClick={() => handleDeleteTeamUser(teamUser)}>
                  {formatMessage({ id: 'team-form.delete-user' })}
                </MenuItem>
              </Menu>
            </ListItemSecondaryAction>
          )
        )}
      </PermissionsViewDecider>
    </ListItem>
  );
};
